/**
 * Asks the browser to replay the outbox in the background. Section 9 rule 3.
 *
 * "Where supported" is doing real work in that sentence: Background Sync is
 * Chromium only. Safari and Firefox have neither the registration API nor the
 * event, which on this project means every iPhone on the floor. That is why it
 * is an addition to the three in-page triggers rather than a replacement for
 * them: reconnect, focus and the sweep already cover replay while the app is
 * open, on every browser. This covers the app being closed, on some of them.
 *
 * Every failure path here is silent by design. A browser that refuses the
 * registration, or a user who denied background permission, must not produce an
 * error on a screen: the work is already durably in IndexedDB and the in-page
 * replay will still send it.
 */

export const OUTBOX_SYNC_TAG = 'ceramica-outbox';

/** The registration API, which is not in lib.dom. */
interface SyncManager {
  register(tag: string): Promise<void>;
}

function syncManagerOf(registration: ServiceWorkerRegistration): SyncManager | null {
  const manager = (registration as unknown as { sync?: SyncManager }).sync;
  return manager ?? null;
}

export async function requestBackgroundSync(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) return false;

  try {
    // `ready` rather than `getRegistration`, so a command issued during the
    // first seconds of a cold start waits for the worker instead of silently
    // finding nothing registered yet.
    const registration = await navigator.serviceWorker.ready;
    const manager = syncManagerOf(registration);
    if (manager === null) return false;

    await manager.register(OUTBOX_SYNC_TAG);
    return true;
  } catch {
    return false;
  }
}
